'use client';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { AvailabilityLabel } from '@eliadmin/core/src/components/product/availability-label';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type ProductStockProps = {
  className?: string;
  product?: Product;
  enableTooltip?: boolean;
  enableProductStatusText?: boolean;
};

const APATEKA_SUPPLIER_ID = 1;
const APATEKA_SUPPLIER_NAME = 'Apateka';

export function ProductStock({
  className,
  enableTooltip = false,
  enableProductStatusText = false,
}: ProductStockProps) {
  const { t } = useTranslations();
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const product = useVariantsStore(state => state.product);

  const entityToCompare = selectedVariant ?? product;

  if (!entityToCompare && !product) return null;

  const isProductEnded = product.status === 'ended';
  const isProductArrival = product.status === 'arrival';

  if (enableProductStatusText && (isProductEnded || isProductArrival)) {
    return (
      <span
        className={cn(
          'text-lg font-bold',
          isProductEnded ? 'text-destructive-foreground' : 'text-info',
          isProductEnded ? 'ea-product-stock__ended-text' : 'ea-product-stock__arrival-text',
          className,
        )}
      >
        {t(isProductEnded ? 'text3789' : 'text3790')}
      </span>
    );
  }

  const apatekaSupplier = product.suppliers?.find(
    s => s.supplier.id === APATEKA_SUPPLIER_ID || s.supplier.name === APATEKA_SUPPLIER_NAME,
  );

  const availability = apatekaSupplier
    ? apatekaSupplier.availability
    : (selectedVariant?.availability ?? product.availability);

  const unavailability = apatekaSupplier
    ? apatekaSupplier.unavailability
    : (selectedVariant?.unavailability ?? product.unavailability);

  const quantity = apatekaSupplier ? apatekaSupplier.quantity : entityToCompare.quantity;

  const minQuantity = product.min_quantity ?? 1;
  const isSellable =
    quantity > 0 && quantity >= minQuantity
      ? true
      : availability?.sellable
        ? true
        : unavailability?.sellable
          ? true
          : false;

  return quantity >= minQuantity || availability?.sellable ? (
    <AvailabilityLabel
      isAvailable={isSellable}
      customName={availability?.name}
      customColor={availability?.color}
      customColorText={availability?.color_text}
      className={className}
      tooltipText={enableTooltip ? availability?.description : undefined}
    />
  ) : (
    <AvailabilityLabel
      isAvailable={isSellable}
      customName={unavailability?.name}
      customColor={unavailability?.color}
      customColorText={unavailability?.color_text}
      className={className}
      tooltipText={enableTooltip ? unavailability?.description : undefined}
    />
  );
}
